import { useState } from 'react';
import { apiAdminService } from '@api';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useParams } from 'react-router-dom';
import { TAmlCaseLite, TAmlCaseListOptions } from '@xeppt/xeppt-sdk/types';

const CASES_LIMIT = 20;

interface IProps {
    isInitialCases?: boolean;
    isInitialCase?: boolean;
}

export type TAmlCasesFilter = {
    status?: TAmlCaseListOptions['status'];
    caseType?: TAmlCaseListOptions['caseType'];
    from?: string;
    to?: string;
    order: 'asc' | 'desc';
};

const DEFAULT_CASES_FILTER: TAmlCasesFilter = {
    order: 'desc'
};

export const useAmlCases = ({ isInitialCases = false, isInitialCase = false }: IProps) => {
    const [casesOffset, setCasesOffset] = useState(0);
    const [cases, setCases] = useState<TAmlCaseLite[]>([]);
    const [casesTotal, setCasesTotal] = useState(0);
    const [casesFilter, setCasesFilter] = useState<TAmlCasesFilter>(DEFAULT_CASES_FILTER);
    const { id } = useParams<{ id: string }>();

    const { data: casesData, handleRequest: refetchCases } = useApiQuery({
        method: (request: any) => {
            const filter: TAmlCasesFilter = request?.filter ?? casesFilter;
            return apiAdminService.getAmlCases({
                limit: CASES_LIMIT,
                offset: request?.isNew ? 0 : casesOffset,
                status: filter.status,
                caseType: filter.caseType,
                from: filter.from,
                to: filter.to,
                order: filter.order
            });
        },
        onSuccess: (data, request?: any) => {
            if (request?.isNew) {
                setCases(data.items);
                setCasesOffset(data.items.length);
            } else {
                setCases((state) => [...state, ...data.items]);
                setCasesOffset((prev) => prev + data.items.length);
            }
            setCasesTotal(data.total);
        },
        isInitialRequest: isInitialCases
    });

    const { data: amlCase } = useApiQuery({
        method: () => apiAdminService.getAmlCaseById(id || ''),
        condition: !!id,
        deps: [id],
        isInitialRequest: isInitialCase
    });

    const handleChangeCasesFilter = (data: Partial<TAmlCasesFilter>) => {
        const next = { ...casesFilter, ...data };
        setCasesFilter(next);
        setCases([]);
        setCasesOffset(0);
        refetchCases({ isNew: true, filter: next });
    };

    return {
        cases,
        amlCase,
        casesTotal,
        casesData,
        casesFilter,
        refetchCases,
        handleChangeCasesFilter
    };
};
